import { Avatar, Badge, Box, Divider, IconButton, List, ListItemAvatar, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatIcon from '@mui/icons-material/Chat';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { useFetch } from '../../hooks/useFetch';
import useHandleErrors from '../../hooks/useHandleErrors';
import server from '../../global/server';
import { useSnackbar } from 'notistack';

// Paleta WhatsApp
const WA = {
    header: '#f0f2f5',
    headerText: '#111b21',
    headerSub: '#667781',
    chatBg: '#eae6df',
    sentBubble: '#d9fdd3',
    receivedBubble: '#ffffff',
    inputArea: '#f0f2f5',
    inputField: '#ffffff',
    sendBtn: '#00a884',
    timestamp: '#667781',
    icon: '#54656f',
    sidebarBg: '#ffffff',
    sidebarHover: '#f5f6f6',
    sidebarActive: '#f0f2f5',
    divider: '#e9edef',
};

// ─── Message Bubble ──────────────────────────────────────────────────────────
const MessageBubble = ({ message }) => {
    const isOutbound = message.direction === 'outbound';
    const time = new Date(message.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    const statusIcon = {
        sent: '✓',
        delivered: '✓✓',
        read: '✓✓',
        failed: '!',
    }[message.status] || '';

    return (
        <Box sx={{ display: 'flex', justifyContent: isOutbound ? 'flex-end' : 'flex-start', mb: 0.5, px: 2 }}>
            <Box
                sx={{
                    position: 'relative',
                    maxWidth: '65%',
                    bgcolor: isOutbound ? WA.sentBubble : WA.receivedBubble,
                    px: 1.5, pt: 0.75, pb: 0.25,
                    borderRadius: isOutbound ? '8px 0px 8px 8px' : '0px 8px 8px 8px',
                    boxShadow: '0 1px 1px rgba(0,0,0,0.13)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        ...(isOutbound
                            ? { right: -8, borderWidth: '0 0 8px 8px', borderColor: `transparent transparent transparent ${WA.sentBubble}` }
                            : { left: -8, borderWidth: '0 8px 8px 0', borderColor: `transparent ${WA.receivedBubble} transparent transparent` }),
                        borderStyle: 'solid',
                        width: 0, height: 0,
                    },
                }}>
                <Typography sx={{ fontSize: 14, color: '#111', lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {message.body}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
                    <Typography sx={{ fontSize: 11, color: WA.timestamp }}>{time}</Typography>
                    {isOutbound && (
                        <Typography sx={{ fontSize: 11, color: message.status === 'read' ? '#53bdeb' : WA.timestamp, lineHeight: 1 }}>
                            {statusIcon}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ─── Conversation Item ───────────────────────────────────────────────────────
const ConversationItem = ({ conversation, selected, onClick }) => {
    const time = conversation.lastMessageAt
        ? new Date(conversation.lastMessageAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : '';

    return (
        <>
            <ListItemButton
                onClick={onClick}
                sx={{
                    py: 1.5, px: 2,
                    bgcolor: selected ? WA.sidebarActive : 'transparent',
                    '&:hover': { bgcolor: WA.sidebarHover },
                }}>
                <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#dfe5e7', color: WA.icon }}>
                        {(conversation.name || conversation.phone)?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 500, color: WA.headerText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                                {conversation.name || conversation.phone}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: conversation.unreadCount > 0 ? WA.sendBtn : WA.timestamp, flexShrink: 0 }}>
                                {time}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 13, color: WA.headerSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                                {conversation.lastMessage || '...'}
                            </Typography>
                            {conversation.unreadCount > 0 && (
                                <Badge badgeContent={conversation.unreadCount} color="success" sx={{ ml: 1 }} />
                            )}
                        </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    primaryTypographyProps={{ component: 'div' }}
                />
            </ListItemButton>
            <Divider sx={{ borderColor: WA.divider }} />
        </>
    );
};

// ─── Main LiveChat Page ──────────────────────────────────────────────────────
const LiveChat = () => {
    const { socket } = useApp();
    const { enqueueSnackbar } = useSnackbar();
    const [selectedConv, setSelectedConv] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [mobileShowMessages, setMobileShowMessages] = useState(false);
    const bottomRef = useRef(null);

    // Fetch conversations
    const { data: conversations, error: convError, mutate: mutateConversations } = useFetch('backoffice/livechat/conversations');
    useHandleErrors(convError);

    // Fetch messages when conversation is selected
    const { data: fetchedMessages } = useFetch(
        selectedConv ? `backoffice/livechat/conversations/${selectedConv.id}/messages` : null,
        null,
        !selectedConv
    );

    // Sync fetched messages
    useEffect(() => {
        if (fetchedMessages) {
            setMessages(fetchedMessages);
        }
    }, [fetchedMessages]);

    // Auto-scroll
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Socket.io real-time
    useEffect(() => {
        if (!socket) return;

        const handleNewMessage = ({ conversation, message }) => {
            // Atualizar lista de conversas
            mutateConversations();

            // Se a conversa ativa recebeu a mensagem, adicionar
            if (selectedConv && conversation.id === selectedConv.id) {
                setMessages(prev => {
                    if (prev.some(m => m.id === message.id)) return prev;
                    return [...prev, message];
                });
            }
        };

        const handleStatusUpdate = ({ whatsappMsgId, status }) => {
            setMessages(prev =>
                prev.map(m => m.whatsappMsgId === whatsappMsgId ? { ...m, status } : m)
            );
        };

        socket.on('livechat:newMessage', handleNewMessage);
        socket.on('livechat:statusUpdate', handleStatusUpdate);

        return () => {
            socket.off('livechat:newMessage', handleNewMessage);
            socket.off('livechat:statusUpdate', handleStatusUpdate);
        };
    }, [socket, selectedConv, mutateConversations]);

    // Select conversation
    const handleSelectConversation = useCallback((conv) => {
        setSelectedConv(conv);
        setMessages([]);
        setMobileShowMessages(true);
    }, []);

    // Send message
    const handleSend = async () => {
        const text = input.trim();
        if (!text || sending || !selectedConv) return;

        setSending(true);
        try {
            const { data: res } = await server.post(
                `backoffice/livechat/conversations/${selectedConv.id}/messages`,
                { body: text }
            );
            setInput('');
            // Adicionar mensagem otimisticamente (com dedup para evitar duplicata com o socket)
            if (res.data) {
                setMessages(prev => {
                    if (prev.some(m => m.id === res.data.id)) return prev;
                    return [...prev, res.data];
                });
            }
            mutateConversations();
        } catch (error) {
            enqueueSnackbar('Erro ao enviar mensagem', { variant: 'error' });
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // ─── Sidebar: conversation list ──────────────────────────────────────────
    const sidebar = (
        <Box sx={{
            width: { xs: '100%', md: 350 },
            borderRight: `1px solid ${WA.divider}`,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: WA.sidebarBg,
            flexShrink: 0,
        }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: WA.header, borderBottom: `1px solid ${WA.divider}` }}>
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: WA.headerText }}>
                    Live Chat
                </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {(!conversations || conversations.length === 0) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 8, color: WA.headerSub }}>
                        <ChatIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                        <Typography sx={{ fontSize: 14 }}>Nenhuma conversa</Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {conversations.map(conv => (
                            <ConversationItem
                                key={conv.id}
                                conversation={conv}
                                selected={selectedConv?.id === conv.id}
                                onClick={() => handleSelectConversation(conv)}
                            />
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );

    // ─── Message area ────────────────────────────────────────────────────────
    const messageArea = selectedConv ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: WA.header, borderBottom: `1px solid ${WA.divider}`, flexShrink: 0 }}>
                <IconButton
                    size="small"
                    sx={{ color: WA.icon, display: { xs: 'flex', md: 'none' } }}
                    onClick={() => setMobileShowMessages(false)}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Avatar sx={{ bgcolor: '#dfe5e7', color: WA.icon, width: 40, height: 40 }}>
                    {(selectedConv.name || selectedConv.phone)?.[0]?.toUpperCase() || '?'}
                </Avatar>
                <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: WA.headerText, lineHeight: 1.2 }}>
                        {selectedConv.name || selectedConv.phone}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: WA.headerSub }}>
                        {selectedConv.phone}
                    </Typography>
                </Box>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', py: 1, bgcolor: WA.chatBg, backgroundImage: 'url(/whatsapp_wallpaper.png)', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', flexDirection: 'column' }}>
                {messages.map(msg => (
                    <MessageBubble key={msg.id} message={msg} />
                ))}
                <div ref={bottomRef} />
            </Box>

            {/* Input */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, px: 1.5, py: 1, bgcolor: WA.inputArea, flexShrink: 0 }}>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', bgcolor: WA.inputField, borderRadius: '24px', px: 1.5, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                    <TextField
                        fullWidth
                        placeholder="Digite uma mensagem"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        multiline
                        maxRows={4}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& .MuiInputBase-root': { fontSize: 14, py: 0.75 } }}
                    />
                </Box>
                <IconButton
                    onClick={handleSend}
                    disabled={!input.trim() || sending}
                    sx={{
                        bgcolor: WA.sendBtn, color: '#fff',
                        '&:hover': { bgcolor: '#008f72' },
                        '&.Mui-disabled': { bgcolor: '#ccc' },
                        width: 44, height: 44, flexShrink: 0,
                    }}>
                    <SendIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    ) : (
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: WA.header, color: WA.headerSub }}>
            <ChatIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography sx={{ fontSize: 16 }}>Selecione uma conversa</Typography>
        </Box>
    );

    return (
        <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', overflow: 'hidden' }}>
            {/* Desktop: ambos paineis. Mobile: um ou outro */}
            <Box sx={{ display: { xs: mobileShowMessages ? 'none' : 'flex', md: 'flex' }, flexDirection: 'column', width: { xs: '100%', md: 350 }, flexShrink: 0 }}>
                {sidebar}
            </Box>
            <Box sx={{ display: { xs: mobileShowMessages ? 'flex' : 'none', md: 'flex' }, flex: 1, flexDirection: 'column', minWidth: 0 }}>
                {messageArea}
            </Box>
        </Box>
    );
};

export default LiveChat;
